import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.css";

function buildRowGroups(data, { groupId, headerLabel, groupSortOrder }) {
  const map = new Map();
  for (const row of data) {
    const id = groupId(row);
    if (!map.has(id)) {
      const sortOrder =
        typeof groupSortOrder === "function" ? groupSortOrder(row) : undefined;
      map.set(id, {
        id,
        label: String(headerLabel(row) ?? "").trim() || "—",
        rows: [],
        sortOrder,
      });
    }
    map.get(id).rows.push(row);
  }
  return Array.from(map.values()).sort((a, b) => {
    const soa = a.sortOrder;
    const sob = b.sortOrder;
    if (soa != null && sob != null && soa !== sob) return soa - sob;
    if (soa != null && sob == null) return -1;
    if (soa == null && sob != null) return 1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });
}

function buildNestedGroupTree(rows, configs, depth, pathPrefix) {
  if (!configs?.length || !rows?.length) return [];
  if (depth >= configs.length) return [];
  const cfg = configs[depth];
  const groups = buildRowGroups(rows, cfg);
  const isLast = depth === configs.length - 1;
  return groups.map((g) => {
    const segmentId = g.id;
    const id = pathPrefix ? `${pathPrefix}|${segmentId}` : segmentId;
    if (isLast) {
      return { id, label: g.label, depth, rows: g.rows, subgroups: null };
    }
    return {
      id,
      label: g.label,
      depth,
      subgroups: buildNestedGroupTree(g.rows, configs, depth + 1, id),
      rows: null,
    };
  });
}

function nestedLeafRowCount(node) {
  if (node.rows?.length) return node.rows.length;
  if (node.subgroups?.length) {
    return node.subgroups.reduce((sum, ch) => sum + nestedLeafRowCount(ch), 0);
  }
  return 0;
}

const TableSkeleton = ({ columns, rows }) => {
  const skeletonCellStyle = {
    padding: "16px 24px",
    borderBottom: "1px solid #E5E7EB",
  };
  const skeletonRow = (
    <tr>
      {Array(columns)
        .fill()
        .map((_, index) => (
          <td key={index} className={styles.td} style={skeletonCellStyle}>
            <div className={styles.skeletonStyle}></div>
          </td>
        ))}
    </tr>
  );

  return (
    <tbody>
      {Array(rows)
        .fill()
        .map((_, index) => (
          <React.Fragment key={index}>{skeletonRow}</React.Fragment>
        ))}
    </tbody>
  );
};

const DynamicTable = ({
  headers,
  data,
  actions,
  onRowClick,
  title,
  currentPage,
  totalPages,
  hasNextPage,
  emptyMessage,
  onPageChange,
  isLoading,
  groupByLevels,
  groupByResetKey,
}) => {
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());

  // The backend's total_records ignores the active filter, so a page count derived from it
  // is only trustworthy for an unfiltered list. When the caller can't supply `totalPages`
  // it passes `hasNextPage` instead, derived from whether the last page came back full.
  const nextDisabled =
    hasNextPage != null ? !hasNextPage : currentPage === totalPages;

  // Always give the user a denominator. With a known total, use it. Without one, a short page
  // is provably the last, so the total equals the current page. If another page exists the
  // total is at least currentPage + 1 — shown as "N+" rather than inventing an exact figure.
  const minTotalPages = hasNextPage ? currentPage + 1 : currentPage;
  // An empty page past the first is one step beyond the end — it isn't a page of results,
  // so don't claim "of N" for it.
  const pastEnd = data.length === 0 && currentPage > 1;
  const pageLabel = pastEnd
    ? `Page ${currentPage}`
    : totalPages
      ? `Page ${currentPage} of ${totalPages}`
      : `Page ${currentPage} of ${minTotalPages}${hasNextPage ? "+" : ""}`;

  useEffect(() => {
    setExpandedGroupIds(new Set());
  }, [groupByResetKey]);

  const toggleGroup = useCallback((gid) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  }, []);

  const nestedGroupTree = useMemo(() => {
    if (!groupByLevels?.length || !data?.length) return null;
    return buildNestedGroupTree(data, groupByLevels, 0, "");
  }, [data, groupByLevels]);

  const colCount = headers.length + (actions && actions.length > 0 ? 1 : 0);

  const handleActionClick = (e, action, row) => {
    e.stopPropagation();
    action.onClick(row);
  };

  const renderDataRow = (row, rowIndex) => (
    <tr
      key={row.id != null ? `row-${row.id}` : `row-${rowIndex}`}
      className={styles.row}
      onClick={() => onRowClick?.(row)}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#f8fafc";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "";
      }}
    >
      {headers.map((header, cellIndex) => (
        <td key={cellIndex} className={styles.td} data-label={header.label}>
          {header.key === "patient_id" ? (
            <div className={styles.avatarName}>
              {row.avatar && (
                <img src={row.avatar} alt={row.patient_id[1]} className={styles.avatar} />
              )}
              <span>{row.patient_id[1]}</span>
            </div>
          ) : (
            row[header.key]
          )}
        </td>
      ))}
      {actions && actions.length > 0 && (
        <td className={styles.actionCell}>
          <div className={styles.actionButtons}>
            {actions.map((action, actionIndex) =>
              action.showIf && action.showIf(row) ? (
                <button
                  key={actionIndex}
                  type="button"
                  onClick={(e) => handleActionClick(e, action, row)}
                  className={styles.actionButton}
                >
                  {action.label}
                </button>
              ) : null,
            )}
          </div>
        </td>
      )}
    </tr>
  );

  const renderGroupNodes = (nodes) => {
    if (!nodes?.length) return null;
    return nodes.map((node) => {
      const open = expandedGroupIds.has(node.id);
      const count = nestedLeafRowCount(node);
      const padLeft = 16 + node.depth * 20;
      return (
        <React.Fragment key={node.id}>
          <tr className={styles.groupHeaderRow} onClick={() => toggleGroup(node.id)}>
            <td
              className={styles.groupHeaderCell}
              colSpan={colCount}
              style={{ paddingLeft: padLeft }}
            >
              <span className={styles.groupChevron} aria-hidden>
                {open ? "▼" : "▶"}
              </span>
              {node.label}{" "}
              <span style={{ fontWeight: 500, color: "#6b7280" }}>({count})</span>
            </td>
          </tr>
          {open && (
            <>
              {node.subgroups && renderGroupNodes(node.subgroups)}
              {node.rows && node.rows.map((row, i) => renderDataRow(row, i))}
            </>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className={styles.mainContainer}>
      {title && <div className={styles.title}>{title}</div>}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={index} className={styles.th}>
                  {header.label}
                </th>
              ))}
              {actions && actions.length > 0 && <th className={styles.th}>Actions</th>}
            </tr>
          </thead>
          {isLoading ? (
            <TableSkeleton columns={headers.length + (actions ? 1 : 0)} rows={10} />
          ) : nestedGroupTree ? (
            <tbody>{renderGroupNodes(nestedGroupTree)}</tbody>
          ) : (
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={colCount}
                    style={{
                      padding: "48px 24px",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: 14,
                    }}
                  >
                    {emptyMessage || "No records match the current filters."}
                  </td>
                </tr>
              ) : (
                data.map((row, rowIndex) => renderDataRow(row, rowIndex))
              )}
            </tbody>
          )}
        </table>
      </div>
      {(data.length > 0 || currentPage > 1) && !isLoading && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            {pageLabel}
          </span>
          <button
            className={styles.pageButton}
            style={{ opacity: nextDisabled ? 0.5 : 1 }}
            onClick={() => onPageChange(currentPage + 1)}
            disabled={nextDisabled}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default DynamicTable;
